import { ValueLoading } from '#components/ValueLoading.tsx'
import { useAuth } from '#context/Auth.tsx'
import type { TeamId } from '@nrfcloud/account-service-proto/domain'
import {
	type TeamResponse,
	DefaultExperience,
} from '@nrfcloud/account-service-proto/user'
import type { StaticDecode } from '@sinclair/typebox'
import { useEffect, useState } from 'preact/hooks'

export const TeamCard = ({
	id,
	profile,
}: {
	id: TeamId
	profile?: {
		team: StaticDecode<typeof TeamResponse>
		version: string
	}
}) => {
	const { oidcUser } = useAuth()
	const [defaultExperience, setDefaultExperience] =
		useState<DefaultExperience>()
	const [isUpdating, setIsUpdating] = useState(false)
	const [version, setVersion] = useState<string>(profile?.version ?? '')

	useEffect(() => {
		if (profile === undefined) return
		if (profile?.team.defaultExperience !== undefined) {
			setDefaultExperience(profile.team.defaultExperience)
		}
		setVersion(profile.version)
	}, [profile])

	useEffect(() => {
		if (profile === undefined) return
		if (oidcUser?.id_token === undefined) return
		if (defaultExperience === undefined) return
		if (defaultExperience === profile.team.defaultExperience) return
		fetch(new URL(`/2025-06-30/team/${id}`, ACCOUNT_API_URL), {
			method: 'PATCH',
			body: JSON.stringify({ defaultExperience }),
			headers: {
				'Content-Type': 'application/json',
				Authorization: oidcUser.id_token,
				'If-Match': version?.toString() ?? '',
			},
		})
			.then((res) => setVersion(res.headers.get('Etag') ?? ''))
			.finally(() => setIsUpdating(false))
			.catch(console.error)
	}, [profile, defaultExperience])

	return (
		<div class="card border-0 shadow rounded-3 mb-4">
			<div class="card-header bg-white p-4 pb-0">
				<h4 className="text-dark fs-4">
					<ValueLoading value={profile} height={20} width={250}>
						<>{profile?.team.name ?? <em>Unnamed team</em>}</>
					</ValueLoading>
				</h4>
				<p>
					<abbr title="The Team ID is used the unique identifier for your team.">
						Team ID
					</abbr>
					: <code>{id}</code>
				</p>
			</div>
			<div class="card-body p-4">
				<div class="mb-3">
					<label class="form-label">Default Platform Experience</label>
					<div class="form-check">
						<input
							class="form-check-input"
							type="radio"
							name="defaultExperience"
							id="experienceNRF5"
							value={DefaultExperience.NRF5}
							checked={defaultExperience === DefaultExperience.NRF5}
							onChange={() => setDefaultExperience(DefaultExperience.NRF5)}
							disabled={isUpdating || profile === undefined}
						/>
						<label class="form-check-label" for="experienceNRF5">
							{DefaultExperience.NRF5}
						</label>
					</div>
					<div class="form-check">
						<input
							class="form-check-input"
							type="radio"
							name="defaultExperience"
							id="experienceNRF9"
							value={DefaultExperience.NRF9}
							checked={defaultExperience === DefaultExperience.NRF9}
							onChange={() => setDefaultExperience(DefaultExperience.NRF9)}
							disabled={isUpdating || profile === undefined}
						/>
						<label class="form-check-label" for="experienceNRF9">
							{DefaultExperience.NRF9}
						</label>
					</div>
				</div>
			</div>
		</div>
	)
}
